import PropTypes from "prop-types";
import {
  Badge,
  Card,
  CardBody,
  CardHeader,
  Col,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
} from "reactstrap";
import dayjs from "dayjs";
// import BookingAttendanceScanner from "./BookingAttendanceScanner";

const formatGbp = (value) =>
  value != null && value !== "" ? `£${Number(value).toFixed(2)}` : "-";

function renderSupervisorList(supervisors = []) {
  if (!supervisors?.length) return <span>-</span>;
  return (
    <ListGroup flush className="mt-2">
      {supervisors.map((sup, idx) => (
        <ListGroupItem key={`${sup.email}-${idx}`} className="px-0 border-0">
          <div className="fw-semibold">{sup.fullName || "-"}</div>
          <small className="text-muted d-block">{sup.email || "-"}</small>
          <small className="text-muted d-block">{sup.phoneNumber || "-"}</small>
        </ListGroupItem>
      ))}
    </ListGroup>
  );
}

export default function BookingDetailsTab({ booking }) {
  return (
    <Row className="gy-3">
      <Col lg={8}>
        <Card>
          <CardHeader>
            <h5 className="mb-0">Booking Overview</h5>
          </CardHeader>
          <CardBody>
            <Row className="gy-3">
              <Col md={6}>
                <small className="text-muted d-block">Booking ID</small>
                <div className="fw-semibold">{booking.slug || "-"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">Booking Type</small>
                <div className="text-capitalize">
                  {booking.bookingType?.replace(/_/g, " ") || "-"}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">Created On</small>
                <div>
                  {booking.createdAt
                    ? dayjs(booking.createdAt).format("DD MMM YYYY, HH:mm")
                    : "-"}
                </div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">Notes</small>
                <div>{booking.notes || "None"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">PO Reference</small>
                <div>{booking.poReference || "-"}</div>
              </Col>
              <Col md={6}>
                <small className="text-muted d-block">PO Required</small>
                <div>{booking.hasPORequired ? "Yes" : "No"}</div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="mt-3">
          <CardHeader>
            <h5 className="mb-0">Shift Pattern</h5>
          </CardHeader>
          <CardBody>
            {Array.isArray(booking.shiftPattern) &&
            booking.shiftPattern.length > 0 ? (
              booking.shiftPattern.map((shift, idx) => (
                <Card key={idx} className="mb-3 border shadow-none">
                  <CardHeader className="bg-light d-flex justify-content-between align-items-center">
                    <div>
                      <div className="fw-semibold text-capitalize">
                        {shift.role?.title || "Role not specified"}
                      </div>
                      <small className="text-muted">
                        Quantity: {shift.quantity ?? "-"} | Sector:{" "}
                        {shift.sector?.title || "-"}
                      </small>
                    </div>
                    <Badge className="bg-secondary">
                      {shift.startTime} - {shift.endTime}
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    <Row className="gy-3">
                      <Col md={4}>
                        <small className="text-muted d-block">Break Time</small>
                        <div>{shift.breakTime || "-"}</div>
                      </Col>
                      {/* Base rate hidden from client */}
                      {/* <Col md={3}>
                        <small className="text-muted d-block">Base Rate</small>
                        <div>{formatGbp(shift.workerBaseRate)}</div>
                      </Col> */}
                      <Col md={4}>
                        <small className="text-muted d-block">Worker Rate</small>
                        <div>{formatGbp(shift.workerRate)}</div>
                      </Col>
                      <Col md={4}>
                        <small className="text-muted d-block">Shift Price</small>
                        <div>{formatGbp(shift.price)}</div>
                      </Col>
                      <Col md={12}>
                        <small className="text-muted d-block">PPE</small>
                        <div>
                          {shift.hasPPEProvided ? "Provided" : "Not provided"}
                        </div>
                        {shift.ppeInstruction && (
                          <div className="text-muted small">
                            {shift.ppeInstruction}
                          </div>
                        )}
                      </Col>
                      <Col md={12}>
                        <small className="text-muted d-block">
                          Shift Details
                        </small>
                        <div>{shift.shiftDetails || "-"}</div>
                      </Col>
                      <Col md={6}>
                        <small className="text-muted d-block">Supervisors</small>
                        {renderSupervisorList(
                          shift.supervisors || shift.supervisor || []
                        )}
                      </Col>
                      <Col md={6}>
                        <small className="text-muted d-block">Dates</small>
                        <Table responsive className="table-sm mb-0">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th className="text-end">Remaining</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(shift.dates || []).map((dateInfo, dateIdx) => (
                              <tr key={dateIdx}>
                                <td>
                                  {dateInfo.date
                                    ? dayjs(dateInfo.date).format(
                                        "DD MMM YYYY"
                                      )
                                    : "-"}
                                </td>
                                <td className="text-end">
                                  {dateInfo.remaining ?? "-"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ))
            ) : (
              <div className="text-muted">No shift pattern available.</div>
            )}
          </CardBody>
        </Card>
      </Col>

      <Col lg={4}>
        <Card>
          <CardHeader>
            <h5 className="mb-0">Location</h5>
          </CardHeader>
          <CardBody>
            <div className="fw-semibold">
              {booking.location?.siteName || "-"}
            </div>
            <div className="text-muted">
              {booking.location?.fullAddress ||
                booking.location?.searchAddress ||
                "-"}
            </div>
            {booking.location?.latitude && booking.location?.longitude && (
              <small className="text-muted d-block mt-2">
                Lat: {booking.location.latitude} | Lon:{" "}
                {booking.location.longitude}
              </small>
            )}
          </CardBody>
        </Card>

        <Card className="mt-3">
          <CardHeader>
            <h5 className="mb-0">Created By</h5>
          </CardHeader>
          <CardBody>
            <div className="fw-semibold">
              {booking.createdBy?.fullName || "-"}
            </div>
            <div className="text-muted">{booking.createdBy?.email || "-"}</div>
          </CardBody>
        </Card>

        {/* Worker Attendance Scanner hidden for now */}
        {/* <BookingAttendanceScanner booking={booking} /> */}
      </Col>
    </Row>
  );
}

BookingDetailsTab.propTypes = {
  booking: PropTypes.object.isRequired,
};
